<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Invoice from 'dashboard/api/invoice';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  enterprise: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();

const isOpen = computed({
  get: () => props.show,
  set: value => {
    if (!value) emit('close');
  },
});

const isEditMode = computed(() => !!props.enterprise);

// 表单数据
const formData = ref({
  enterprise_name: '',
  tax_no: '',
  address: '',
  phone: '',
  bank_name: '',
  bank_account: '',
  email: '',
  is_default: false,
});

const isSubmitting = ref(false);
const submitError = ref('');

// 表单验证
const validateForm = () => {
  if (!formData.value.enterprise_name.trim()) {
    submitError.value = t(
      'BILLING_INVOICES.ENTERPRISE.ENTERPRISE_NAME_REQUIRED'
    );
    return false;
  }
  if (!formData.value.tax_no.trim()) {
    submitError.value = t('BILLING_INVOICES.ENTERPRISE.TAX_NO_REQUIRED');
    return false;
  }
  if (!formData.value.email.trim()) {
    submitError.value = t('BILLING_INVOICES.ENTERPRISE.EMAIL_REQUIRED');
    return false;
  }
  return true;
};

// 提交表单
const submitForm = async () => {
  submitError.value = '';

  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    if (isEditMode.value) {
      await Invoice.updateEnterpriseInfo(props.enterprise.id, formData.value);
    } else {
      await Invoice.createEnterpriseInfo(formData.value);
    }

    emit('success');
    isOpen.value = false;
  } catch (error) {
    submitError.value =
      error.response?.data?.error ||
      t('BILLING_INVOICES.ENTERPRISE.SUBMIT_ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

// 重置表单
const resetForm = () => {
  formData.value = {
    enterprise_name: '',
    tax_no: '',
    address: '',
    phone: '',
    bank_name: '',
    bank_account: '',
    email: '',
    is_default: false,
  };
  submitError.value = '';
};

// 加载企业信息
watch(
  () => props.enterprise,
  enterprise => {
    if (enterprise) {
      formData.value = {
        enterprise_name: enterprise.enterprise_name || '',
        tax_no: enterprise.tax_no || '',
        address: enterprise.address || '',
        phone: enterprise.phone || '',
        bank_name: enterprise.bank_name || '',
        bank_account: enterprise.bank_account || '',
        email: enterprise.email || '',
        is_default: enterprise.is_default || false,
      };
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

watch(isOpen, newValue => {
  if (newValue && !isEditMode.value) {
    resetForm();
  }
});
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
  >
    <div
      class="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      @click.stop
    >
      <!-- 头部 -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{
            isEditMode
              ? $t('BILLING_INVOICES.ENTERPRISE.EDIT_TITLE')
              : $t('BILLING_INVOICES.ENTERPRISE.ADD_TITLE')
          }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-slate-3 transition-colors"
          @click="isOpen = false"
        >
          <span class="i-lucide-x text-n-slate-11 text-xl" />
        </button>
      </div>

      <!-- 内容 -->
      <div class="p-6 space-y-4">
        <!-- 错误提示 -->
        <div v-if="submitError" class="p-4 text-red-600 bg-red-50 rounded-lg">
          {{ submitError }}
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.ENTERPRISE_NAME') }} *
            </label>
            <input
              v-model="formData.enterprise_name"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="
                $t('BILLING_INVOICES.ENTERPRISE.ENTERPRISE_NAME_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.TAX_NO') }} *
            </label>
            <input
              v-model="formData.tax_no"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="
                $t('BILLING_INVOICES.ENTERPRISE.TAX_NO_PLACEHOLDER')
              "
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('BILLING_INVOICES.ENTERPRISE.ADDRESS') }}
          </label>
          <input
            v-model="formData.address"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            :placeholder="$t('BILLING_INVOICES.ENTERPRISE.ADDRESS_PLACEHOLDER')"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.PHONE') }}
            </label>
            <input
              v-model="formData.phone"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="$t('BILLING_INVOICES.ENTERPRISE.PHONE_PLACEHOLDER')"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.EMAIL') }} *
            </label>
            <input
              v-model="formData.email"
              type="email"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="$t('BILLING_INVOICES.ENTERPRISE.EMAIL_PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.BANK_NAME') }}
            </label>
            <input
              v-model="formData.bank_name"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="
                $t('BILLING_INVOICES.ENTERPRISE.BANK_NAME_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('BILLING_INVOICES.ENTERPRISE.BANK_ACCOUNT') }}
            </label>
            <input
              v-model="formData.bank_account"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :placeholder="
                $t('BILLING_INVOICES.ENTERPRISE.BANK_ACCOUNT_PLACEHOLDER')
              "
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="isDefault"
            v-model="formData.is_default"
            type="checkbox"
            class="w-4 h-4 text-woot-500 rounded"
          />
          <label for="isDefault" class="text-sm text-n-slate-12 cursor-pointer">
            {{ $t('BILLING_INVOICES.ENTERPRISE.SET_AS_DEFAULT') }}
          </label>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak bg-n-slate-1"
      >
        <Button
          variant="secondary"
          :label="$t('BILLING_INVOICES.ENTERPRISE.CANCEL')"
          @click="isOpen = false"
        />
        <Button
          :label="
            isEditMode
              ? $t('BILLING_INVOICES.ENTERPRISE.UPDATE')
              : $t('BILLING_INVOICES.ENTERPRISE.SAVE')
          "
          :is-loading="isSubmitting"
          @click="submitForm"
        />
      </div>
    </div>
  </div>
</template>
